import { Injectable } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(private keycloakService: KeycloakService) {}

  async isLoggedIn(): Promise<boolean> {
    return this.keycloakService.isLoggedIn();
  }

  async getUserRoles(): Promise<string[]> {
    try {
      // Primeiro tenta obter roles reais do Keycloak
      const realRoles = await this.keycloakService.getUserRoles();

      // Se há roles reais e incluem ESTOKAI_OWNER, retorna as roles reais
      if (
        realRoles &&
        realRoles.length > 0 &&
        realRoles.includes('ESTOKAI_OWNER')
      ) {
        console.log('🔑 ESTOKAI_OWNER detected - using real roles:', realRoles);
        return realRoles;
      }

      // Se há roles reais mas não é ESTOKAI_OWNER, retorna as roles reais
      if (realRoles && realRoles.length > 0) {
        console.log('🔑 Real user roles detected:', realRoles);
        return realRoles;
      }

      // Fallback para development - simular ESTOKAI_OWNER para testes
      console.log(
        '🔑 No real roles found - using mock ESTOKAI_OWNER for development'
      );
      const mockRoles = ['ESTOKAI_OWNER'];
      return mockRoles;
    } catch (error) {
      console.error('Error getting user roles:', error);
      // Fallback para development em caso de erro - simular ESTOKAI_OWNER
      return ['ESTOKAI_OWNER'];
    }
  }

  async getUsername(): Promise<string> {
    const userProfile = await this.keycloakService.loadUserProfile();
    return userProfile.username || '';
  }

  // Método para obter a role ativa (considerando o ProfileViewService)
  private getActiveRole(): string | null {
    try {
      // Usar método estático para evitar dependência circular
      const ProfileViewService = (globalThis as any)
        .__profileViewServiceInstance;
      if (ProfileViewService) {
        const isViewModeActive = ProfileViewService.isViewModeActive?.value;
        const currentViewProfile = ProfileViewService.currentViewProfile?.value;

        console.log(
          '🔄 Getting active role - View mode:',
          isViewModeActive,
          'Profile:',
          currentViewProfile
        );

        // Se estiver em modo visualização, usar a role da visualização
        if (
          isViewModeActive &&
          currentViewProfile &&
          currentViewProfile !== 'ESTOKAI_OWNER'
        ) {
          console.log('👀 Using view mode profile:', currentViewProfile);
          return currentViewProfile;
        }
      }
    } catch (error) {
      console.warn('Error getting active role from ProfileViewService:', error);
    }

    return null; // Sem role ativa, usar fallback
  }

  async hasRole(role: string): Promise<boolean> {
    // Verificar primeiro se é ESTOKAI_OWNER real (sempre tem prioridade)
    const realRoles = await this.getUserRoles();
    const isRealEstokaiOwner = realRoles.includes('ESTOKAI_OWNER');

    if (isRealEstokaiOwner) {
      // ESTOKAI_OWNER tem acesso a todas as roles
      console.log(`🔑 ESTOKAI_OWNER checking role '${role}' - always granted`);
      return true;
    }

    // Para usuários não ESTOKAI_OWNER, verificar role ativa (modo visualização)
    const activeRole = this.getActiveRole();

    if (activeRole) {
      return activeRole === role;
    }

    // Fallback: verificar roles reais do usuário
    return realRoles.includes(role);
  }

  // Verifica se é ESTOKAI_OWNER (super admin) - sempre verifica a role real
  async isEstokaiOwner(): Promise<boolean> {
    const realRoles = await this.getUserRoles();
    return realRoles.includes('ESTOKAI_OWNER');
  }

  // New role verification methods to match the menu system
  async isOwner(): Promise<boolean> {
    return this.hasRole('owner');
  }

  async isFinancialManager(): Promise<boolean> {
    return this.hasRole('financial_manager');
  }

  async isSalesUser(): Promise<boolean> {
    return this.hasRole('sales_user');
  }

  async isStockManager(): Promise<boolean> {
    return this.hasRole('stock_manager');
  }

  async isPartner(): Promise<boolean> {
    return this.hasRole('partner');
  }

  // Legacy role support for backwards compatibility
  async isDono(): Promise<boolean> {
    return this.hasRole('dono') || this.hasRole('owner');
  }

  async isGerente(): Promise<boolean> {
    return (
      this.hasRole('gerente') ||
      this.hasRole('financial_manager') ||
      this.hasRole('stock_manager')
    );
  }

  async isVendedor(): Promise<boolean> {
    return this.hasRole('vendedor') || this.hasRole('sales_user');
  }

  async isSocio(): Promise<boolean> {
    return this.hasRole('socio') || this.hasRole('partner');
  }

  // Métodos que consideram a role ativa (visualização) - usando legacy names
  async isManager(): Promise<boolean> {
    return this.isGerente();
  }

  async isSeller(): Promise<boolean> {
    return this.isVendedor();
  }

  // Verifica se tem acesso administrativo (ESTOKAI_OWNER ou owner)
  async hasAdminAccess(): Promise<boolean> {
    const isEstokaiOwner = await this.isEstokaiOwner();
    const isStoreOwner = await this.isOwner();
    return isEstokaiOwner || isStoreOwner;
  }

  // Verifica se tem acesso gerencial (admin, financial_manager, stock_manager ou partner)
  async hasManagerAccess(): Promise<boolean> {
    const hasAdmin = await this.hasAdminAccess();
    const isFinancialManager = await this.isFinancialManager();
    const isStockManager = await this.isStockManager();
    const isPartner = await this.isPartner();
    return hasAdmin || isFinancialManager || isStockManager || isPartner;
  }

  // Verifica se pode acessar uma funcionalidade específica
  async canAccess(feature: string): Promise<boolean> {
    // Primeiro verificar se é ESTOKAI_OWNER real (não em modo visualização)
    const realRoles = await this.getUserRoles();
    const isRealEstokaiOwner = realRoles.includes('ESTOKAI_OWNER');

    // Se há ProfileViewService disponível, usar sua lógica
    try {
      const ProfileViewService = (globalThis as any)
        .__profileViewServiceInstance;
      if (ProfileViewService) {
        return await ProfileViewService.canAccessFeature(feature);
      }
    } catch (error) {
      console.warn(
        'Error checking feature access with ProfileViewService:',
        error
      );
    }

    // Fallback: lógica original
    if (isRealEstokaiOwner) {
      return true;
    }

    // Mapeamento de funcionalidades por role (usando role ativa)
    const rolePermissions = {
      dashboard: [
        'owner',
        'financial_manager',
        'stock_manager',
        'sales_user',
        'partner',
        'dono',
        'gerente',
        'socio',
        'vendedor',
      ],
      products: [
        'owner',
        'financial_manager',
        'stock_manager',
        'sales_user',
        'dono',
        'gerente',
        'socio',
      ],
      categories: [
        'owner',
        'financial_manager',
        'stock_manager',
        'dono',
        'gerente',
      ],
      brands: [
        'owner',
        'financial_manager',
        'stock_manager',
        'dono',
        'gerente',
      ],
      sales: [
        'owner',
        'financial_manager',
        'sales_user',
        'partner',
        'dono',
        'gerente',
        'socio',
        'vendedor',
      ],
      suppliers: [
        'owner',
        'financial_manager',
        'stock_manager',
        'dono',
        'gerente',
      ],
      reports: [
        'owner',
        'financial_manager',
        'partner',
        'dono',
        'gerente',
        'socio',
      ],
      users: ['owner', 'dono'], // Apenas owner pode gerenciar usuários
      integrations: ['owner', 'dono'], // Apenas owner pode gerenciar integrações
      campaigns: ['owner', 'financial_manager', 'dono', 'gerente'],
      financial: [
        'owner',
        'financial_manager',
        'partner',
        'dono',
        'gerente',
        'socio',
      ],
    };

    const allowedRoles =
      rolePermissions[feature as keyof typeof rolePermissions] || [];

    // Usar role ativa se disponível
    const activeRole = this.getActiveRole();
    if (activeRole) {
      return allowedRoles.includes(activeRole);
    }

    // Fallback: verificar roles reais
    const userRoles = await this.getUserRoles();
    return allowedRoles.some((role) => userRoles.includes(role));
  }

  // Verifica se pode selecionar lojas (OWNER e superiores na hierarquia)
  async canSelectStore(): Promise<boolean> {
    const isEstokaiOwner = await this.isEstokaiOwner();
    const isOwner = await this.isOwner();
    return isEstokaiOwner || isOwner;
  }

  logout() {
    this.keycloakService.logout(window.location.origin);
  }

  async getToken(): Promise<string> {
    return this.keycloakService.getToken() || '';
  }

  getCurrentUser(): { name?: string; role?: string } | null {
    try {
      // Mock user for development - testing ESTOKAI_OWNER access
      return {
        name: 'Super Admin',
        role: 'ESTOKAI_OWNER', // Testing with ESTOKAI_OWNER
      };
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }
}
